// Type-safe API client. All methods return Promise<T> and throw ApiError on
// non-2xx responses. 401 auto-redirects to /login.

import type {
  AdminSettings,
  AdminSettingsPatch,
  AnalysisKeywordsResponse,
  AnalysisTimelineResponse,
  ApiKey,
  Consumer,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  AdminTopicDetailResponse,
  AdminTopicsResponse,
  CreateConsumerInput,
  CreateSourceInput,
  CreateTopicWorkerInput,
  ItemsFilter,
  ItemsResponse,
  JobRunsFilter,
  JobRunsResponse,
  JobStats,
  RunSourceResult,
  Source,
  Stats,
  StorageStats,
  Topic,
  TopicWorker,
  UpdateTopicWorkerInput,
  UpdateSourceInput,
  User,
  VersionInfo,
} from './types'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (res.status === 401) {
    if (!location.pathname.startsWith('/login')) {
      window.location.href = '/login'
    }
    throw new ApiError(401, 'unauthorized')
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(
      res.status,
      (body as { error?: string } | null)?.error ?? res.statusText,
      body,
    )
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ─── API ──────────────────────────────────────────────────────────────────

export const api = {
  // Auth
  login: (email: string, password: string) =>
    request<User>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<User>('/auth/me'),

  // Meta
  health: () => request<{ ok: boolean }>('/api/health'),
  version: () => request<VersionInfo>('/api/version'),
  stats: () => request<Stats>('/api/stats'),

  // Topics (admin uses the consumer endpoint via cookie session — but
  // /api/topics is API-key gated, so we don't expose it to the dashboard.
  // Instead the dashboard uses a tiny SQL-derived listing under stats. To
  // keep the surface small for now we keep this client method un-used; the
  // dashboard renders its preview from the stats-extended response.)
  topics: () => request<{ data: Topic[]; last_clustered_at: number }>('/api/topics'),

  // Topics admin
  listAdminTopics: (params: { limit?: number; offset?: number; q?: string; spike?: boolean; worker?: string; labeled?: string } = {}) => {
    const p = new URLSearchParams()
    if (params.limit) p.set('limit', String(params.limit))
    if (params.offset) p.set('offset', String(params.offset))
    if (params.q) p.set('q', params.q)
    if (params.spike) p.set('spike', '1')
    if (params.worker) p.set('worker', params.worker)
    if (params.labeled) p.set('labeled', params.labeled)
    return request<AdminTopicsResponse>(`/api/admin/topics?${p}`)
  },
  getAdminTopic: (id: number) => request<AdminTopicDetailResponse>(`/api/admin/topics/${id}`),
  runAllSources: () => request<{ enqueued: number }>('/api/admin/sources/run-all', { method: 'POST' }),
  reseedSources: () => request<{ status: string }>('/api/admin/sources/reseed', { method: 'POST' }),
  relabelTopics: () => request<{ reset: number }>('/api/admin/topics/relabel', { method: 'POST' }),
  reclusterTopic: (id: number) => request<void>(`/api/admin/topics/${id}/recluster`, { method: 'POST' }),
  deleteAdminTopic: (id: number) => request<void>(`/api/admin/topics/${id}`, { method: 'DELETE' }),
  bulkDeleteTopics: (worker: string) =>
    request<{ deleted: number }>(`/api/admin/topics?worker=${encodeURIComponent(worker)}`, { method: 'DELETE' }),

  // Topic workers
  listTopicWorkers: () => request<TopicWorker[]>('/api/admin/topic-workers'),
  createTopicWorker: (input: CreateTopicWorkerInput) =>
    request<{ id: number }>('/api/admin/topic-workers', { method: 'POST', body: JSON.stringify(input) }),
  updateTopicWorker: (id: number, input: UpdateTopicWorkerInput) =>
    request<void>(`/api/admin/topic-workers/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
  reclusterWorker: (id: number) =>
    request<{ deleted_topics: number }>(`/api/admin/topic-workers/${id}/recluster`, { method: 'POST' }),
  deleteTopicWorker: (id: number) =>
    request<void>(`/api/admin/topic-workers/${id}`, { method: 'DELETE' }),

  // Storage stats
  storageStats: () => request<StorageStats>('/api/admin/storage-stats'),

  // Keyword analysis
  getAnalysisKeywords: (params: { window_days?: number; limit?: number } = {}) => {
    const p = new URLSearchParams()
    if (params.window_days) p.set('window_days', String(params.window_days))
    if (params.limit) p.set('limit', String(params.limit))
    return request<AnalysisKeywordsResponse>(`/api/admin/analysis/keywords?${p}`)
  },

  // Keyword timeline (time-aware analytics)
  getAnalysisTimeline: (params: { window_days?: number; bucket?: 'auto' | 'hour' | 'day'; limit?: number } = {}) => {
    const p = new URLSearchParams()
    if (params.window_days) p.set('window_days', String(params.window_days))
    if (params.bucket) p.set('bucket', params.bucket)
    if (params.limit) p.set('limit', String(params.limit))
    return request<AnalysisTimelineResponse>(`/api/admin/analysis/timeline?${p}`)
  },

  // Items
  listItems: (filter: ItemsFilter = {}) => {
    const p = new URLSearchParams()
    if (filter.limit) p.set('limit', String(filter.limit))
    if (filter.offset) p.set('offset', String(filter.offset))
    if (filter.source) p.set('source', filter.source)
    if (filter.tag) p.set('tag', filter.tag)
    if (filter.kind) p.set('kind', filter.kind)
    if (filter.q) p.set('q', filter.q)
    return request<ItemsResponse>(`/api/admin/items?${p}`)
  },

  dedupItems: () => request<{ deleted_items: number; orphaned_topic_items: number; embeddings_reset: boolean }>(
    '/api/admin/items/dedup', { method: 'POST' }),

  // Sources admin
  listSources: () => request<Source[]>('/api/admin/sources'),
  createSource: (input: CreateSourceInput) =>
    request<{ id: number; slug: string }>('/api/admin/sources', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateSource: (id: number, input: UpdateSourceInput) =>
    request<void>(`/api/admin/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  runSource: (id: number) =>
    request<RunSourceResult>(`/api/admin/sources/${id}/run`, { method: 'POST' }),

  // Job runs
  listJobRuns: (filter: JobRunsFilter = {}) => {
    const p = new URLSearchParams()
    if (filter.limit) p.set('limit', String(filter.limit))
    if (filter.offset) p.set('offset', String(filter.offset))
    if (filter.source) p.set('source', filter.source)
    if (filter.outcome) p.set('outcome', filter.outcome)
    return request<JobRunsResponse>(`/api/admin/job-runs?${p}`)
  },
  jobStats: () => request<JobStats>('/api/admin/job-stats'),

  // Consumers
  listConsumers: () => request<Consumer[]>('/api/admin/consumers'),
  createConsumer: (input: CreateConsumerInput) =>
    request<Consumer>('/api/admin/consumers', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteConsumer: (id: number) =>
    request<void>(`/api/admin/consumers/${id}`, { method: 'DELETE' }),

  // API keys
  listApiKeys: (consumerId: number) =>
    request<ApiKey[]>(`/api/admin/consumers/${consumerId}/api-keys`),
  createApiKey: (consumerId: number, input: CreateApiKeyInput) =>
    request<CreateApiKeyResponse>(`/api/admin/consumers/${consumerId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeApiKey: (id: number) =>
    request<void>(`/api/admin/api-keys/${id}`, { method: 'DELETE' }),
  deleteApiKey: (id: number) =>
    request<void>(`/api/admin/api-keys/${id}/hard`, { method: 'DELETE' }),

  // Admin settings
  getAdminSettings: () => request<AdminSettings>('/api/admin/settings'),
  putAdminSettings: (patch: AdminSettingsPatch) =>
    request<AdminSettings>('/api/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
}
